import NBType, { InferType } from '@netbase/nb-type'
import DashboardAutoRefreshInterval from '../enums/DashboardAutoRefreshInterval'
import DashboardOwnerInfoSchema from './DashboardOwnerInfoSchema'
import DashboardPDFFormat from '../enums/DashboardPDFFormat'
import DashboardSharing from '../enums/DashboardSharing'
import DashboardType from '../enums/DashboardType'
import SharingEditing from '../enums/SharingEditing'
// * widgets field related imports
import WidgetSchema from './WidgetSchema'
import WidgetType from '../enums/WidgetType'
import SocialWebWordCloudWidgetSchema from './SocialWebWordCloudWidgetSchema'
import ImageWidgetSchema from './ImageWidgetSchema'
import WhiteSpaceWidgetSchema from './WhiteSpaceWidgetSchema'
import HeaderWidgetSchema from './HeaderWidgetSchema'
import TextWidgetSchema from './TextWidgetSchema'
import SocialWebSentimentWidgetSchema from './SocialWebSentimentWidgetSchema'
import AudienceSentimentWidgetSchema from './AudienceSentimentWidgetSchema'
import SocialWebSourcesWidgetSchema from './SocialWebSourcesWidgetSchema'
import SocialWebDomainsWidgetSchema from './SocialWebDomainsWidgetSchema'
import SocialWebGeoRegionsWidgetSchema from './SocialWebGeoRegionsWidgetSchema'
import AudienceGeoRegionsWidgetSchema from './AudienceGeoRegionsWidgetSchema'
import SocialWebStoryScopeWidgetSchema from './SocialWebStoryScopeWidgetSchema'
import SocialWebSentimentDriversWidgetSchema from './SocialWebSentimentDriversWidgetSchema'
import SocialWebPhotoConceptsWidgetSchema from './SocialWebPhotoConceptsWidgetSchema'
import AudienceWordCloudWidgetSchema from './AudienceWordCloudWidgetSchema'
import AudienceSentimentDriversWidgetSchema from './AudienceSentimentDriversWidgetSchema'
import SocialWebTimelineWidgetSchema from './SocialWebTimelineWidgetSchema'
import SocialWebOldTimelineWidgetSchema from './SocialWebOldTimelineWidgetSchema'
import SocialWebTimelineComparisonWidgetSchema from './SocialWebTimelineComparisonWidgetSchema'
import AudienceTimelineWidgetSchema from './AudienceTimelineWidgetSchema'
import AudienceOldTimelineWidgetSchema from './AudienceOldTimelineWidgetSchema'
import AudienceTimelineComparisonWidgetSchema from './AudienceTimelineComparisonWidgetSchema'
import YoutubeTimelineWidgetSchema from './YoutubeTimelineWidgetSchema'
import SocialWebTopicComparisonWidgetSchema from './SocialWebTopicComparisonWidgetSchema'
import AudienceTopicComparisonWidgetSchema from './AudienceTopicComparisonWidgetSchema'
import SocialWebAuthorsWidgetSchema from './SocialWebAuthorsWidgetSchema'
import AudienceAuthorsWidgetSchema from './AudienceAuthorsWidgetSchema'
import SocialWebIndexWidgetSchema from './SocialWebIndexWidgetSchema'
import SocialWebDemographicWidgetSchema from './SocialWebDemographicWidgetSchema'
import AudienceIndexWidgetSchema from './AudienceIndexWidgetSchema'
import AudienceDemographicWidgetSchema from './AudienceDemographicWidgetSchema'
import SocialWebCrosstabWidgetSchema from './SocialWebCrosstabWidgetSchema'
import AudienceCrosstabWidgetSchema from './AudienceCrosstabWidgetSchema'
import SocialWebDayAndHourWidgetSchema from './SocialWebDayAndHourWidgetSchema'
import AudienceDayAndHourWidgetSchema from './AudienceDayAndHourWidgetSchema'
import SocialWebSummaryMetricsCompactWidgetSchema from './SocialWebSummaryMetricsCompactWidgetSchema'
import AudienceSummaryMetricsCompactWidgetSchema from './AudienceSummaryMetricsCompactWidgetSchema'
import SocialWebStreamWidgetSchema from './SocialWebStreamWidgetSchema'
import AudienceStreamWidgetSchema from './AudienceStreamWidgetSchema'
import YoutubeStreamWidgetSchema from './YoutubeStreamWidgetSchema'
import SocialWebSummaryMetricsWidgetSchema from './SocialWebSummaryMetricsWidgetSchema'
import AudienceSummaryMetricsWidgetSchema from './AudienceSummaryMetricsWidgetSchema'
import YoutubeSummaryMetricsWidgetSchema from './YoutubeSummaryMetricsWidgetSchema'
import SocialWebPopularPostsWidgetSchema from './SocialWebPopularPostsWidgetSchema'
import SocialWebSideBySideWidgetSchema from './SocialWebSideBySideWidgetSchema'
import SocialWebPopularMediaWidgetSchema from './SocialWebPopularMediaWidgetSchema'
import AudiencePopularMediaWidgetSchema from './AudiencePopularMediaWidgetSchema'
import SocialWebBrandPassionIndexWidgetSchema from './SocialWebBrandPassionIndexWidgetSchema'
import RichTextWidgetSchema from './RichTextWidgetSchema'
import AudienceTrendingWidgetSchema from './AudienceTrendingWidgetSchema'
import SocialWebTrendingWidgetSchema from './SocialWebTrendingWidgetSchema'
import SocialWebPopularLinksWidgetSchema from './SocialWebPopularLinksWidgetSchema'
import AudiencePopularLinksWidgetSchema from './AudiencePopularLinksWidgetSchema'
import SocialWebChannelsWidgetSchema from './SocialWebChannelsWidgetSchema'
import SocialWebTagsWidgetSchema from './SocialWebTagsWidgetSchema'
import HTMLWidgetSchema from './HTMLWidgetSchema'
import AudienceLanguageWidgetSchema from './AudienceLanguageWidgetSchema'
import SocialWebLanguageWidgetSchema from './SocialWebLanguageWidgetSchema'
import AudienceUsageAndAttitudeWidgetSchema from './AudienceUsageAndAttitudeWidgetSchema'

const DashboardSchema = NBType.object().shape('DashboardSchema', {
  /* @prop */
  autoRefreshInterval: NBType.enum(DashboardAutoRefreshInterval),
  /* @prop */
  description: NBType.string(),
  /* @prop */
  id: NBType.number(),
  /* @prop */
  labels: NBType.array().of(NBType.string()),
  /* @prop */
  name: NBType.string(),
  /* @prop */
  numOfColumn: NBType.number(),
  /* @prop */
  ownerInfo: DashboardOwnerInfoSchema,
  /* @prop */
  pdfFormat: NBType.enum(DashboardPDFFormat),
  /* @prop */
  sharing: NBType.enum(DashboardSharing),
  /* @prop */
  sharingEditing: NBType.enum(SharingEditing),
  /* @prop */
  type: NBType.enum(DashboardType),
  /* @prop */
  widgets: NBType.array().of(
    // use yup.lazy() to apply schema dynamically
    // Typescript will raise error "Type instantiation is excessively deep and possibly infinite. TS2589" here
    // workaround: give "any" to skip the type infer calculation
    // ref: https://github.com/microsoft/TypeScript/issues/34933
    NBType.lazy((widget): any => {
      switch (widget.type) {
        case WidgetType.SOCIAL_WEB_TIMELINE:
          return SocialWebTimelineWidgetSchema
        case WidgetType.SOCIAL_WEB_TIMELINE_OLD:
          return SocialWebOldTimelineWidgetSchema
        case WidgetType.SOCIAL_WEB_TIMELINE_COMPARISON:
          return SocialWebTimelineComparisonWidgetSchema
        case WidgetType.AUDIENCE_TIMELINE:
          return AudienceTimelineWidgetSchema
        case WidgetType.AUDIENCE_TIMELINE_OLD:
          return AudienceOldTimelineWidgetSchema
        case WidgetType.AUDIENCE_TIMELINE_COMPARISON:
          return AudienceTimelineComparisonWidgetSchema
        case WidgetType.YOUTUBE_TIMELINE:
          return YoutubeTimelineWidgetSchema
        case WidgetType.SOCIAL_WEB_SOURCES:
          return SocialWebSourcesWidgetSchema
        case WidgetType.SOCIAL_WEB_WORDCLOUD:
          return SocialWebWordCloudWidgetSchema
        case WidgetType.SOCIAL_WEB_SENTIMENT_DRIVERS:
          return SocialWebSentimentDriversWidgetSchema
        case WidgetType.SOCIAL_WEB_PHOTO_CONCEPTS:
          return SocialWebPhotoConceptsWidgetSchema
        case WidgetType.AUDIENCE_WORDCLOUD:
          return AudienceWordCloudWidgetSchema
        case WidgetType.AUDIENCE_SENTIMENT_DRIVERS:
          return AudienceSentimentDriversWidgetSchema
        case WidgetType.SOCIAL_WEB_DOMAINS:
          return SocialWebDomainsWidgetSchema
        case WidgetType.IMAGE:
          return ImageWidgetSchema
        case WidgetType.SOCIAL_WEB_SENTIMENT:
          return SocialWebSentimentWidgetSchema
        case WidgetType.AUDIENCE_SENTIMENT:
          return AudienceSentimentWidgetSchema
        case WidgetType.WHITESPACE:
          return WhiteSpaceWidgetSchema
        case WidgetType.HEADER:
          return HeaderWidgetSchema
        case WidgetType.TEXT:
          return TextWidgetSchema
        case WidgetType.SOCIAL_WEB_GEO_REGIONS:
          return SocialWebGeoRegionsWidgetSchema
        case WidgetType.AUDIENCE_GEO_REGIONS:
          return AudienceGeoRegionsWidgetSchema
        case WidgetType.SOCIAL_WEB_STORY_SCOPE:
          return SocialWebStoryScopeWidgetSchema
        case WidgetType.SOCIAL_WEB_TOPIC_COMPARISON:
          return SocialWebTopicComparisonWidgetSchema
        case WidgetType.AUDIENCE_TOPIC_COMPARISON:
          return AudienceTopicComparisonWidgetSchema
        case WidgetType.SOCIAL_WEB_AUTHORS:
          return SocialWebAuthorsWidgetSchema
        case WidgetType.AUDIENCE_AUTHORS:
          return AudienceAuthorsWidgetSchema
        case WidgetType.SOCIAL_WEB_INDEX:
          return SocialWebIndexWidgetSchema
        case WidgetType.SOCIAL_WEB_DEMOGRAPHIC:
          return SocialWebDemographicWidgetSchema
        case WidgetType.AUDIENCE_INDEX:
          return AudienceIndexWidgetSchema
        case WidgetType.AUDIENCE_DEMOGRAPHIC:
          return AudienceDemographicWidgetSchema
        case WidgetType.SOCIAL_WEB_CROSSTAB:
          return SocialWebCrosstabWidgetSchema
        case WidgetType.AUDIENCE_CROSSTAB:
          return AudienceCrosstabWidgetSchema
        case WidgetType.SOCIAL_WEB_SUMMARY_METRICS_COMPACT:
          return SocialWebSummaryMetricsCompactWidgetSchema
        case WidgetType.AUDIENCE_SUMMARY_METRICS_COMPACT:
          return AudienceSummaryMetricsCompactWidgetSchema
        case WidgetType.SOCIAL_WEB_SIDE_BY_SIDE:
          return SocialWebSideBySideWidgetSchema
        case WidgetType.SOCIAL_WEB_TRENDING:
          return SocialWebTrendingWidgetSchema
        case WidgetType.AUDIENCE_TRENDING:
          return AudienceTrendingWidgetSchema
        case WidgetType.SOCIAL_WEB_STREAM:
          return SocialWebStreamWidgetSchema
        case WidgetType.AUDIENCE_STREAM:
          return AudienceStreamWidgetSchema
        case WidgetType.YOUTUBE_STREAM:
          return YoutubeStreamWidgetSchema
        case WidgetType.SOCIAL_WEB_SUMMARY_METRICS:
          return SocialWebSummaryMetricsWidgetSchema
        case WidgetType.AUDIENCE_SUMMARY_METRICS:
          return AudienceSummaryMetricsWidgetSchema
        case WidgetType.YOUTUBE_SUMMARY_METRICS:
          return YoutubeSummaryMetricsWidgetSchema
        case WidgetType.SOCIAL_WEB_POPULAR_POSTS:
          return SocialWebPopularPostsWidgetSchema
        case WidgetType.SOCIAL_WEB_POPULAR_MEDIA:
          return SocialWebPopularMediaWidgetSchema
        case WidgetType.AUDIENCE_POPULAR_MEDIA:
          return AudiencePopularMediaWidgetSchema
        case WidgetType.SOCIAL_WEB_DAY_AND_HOUR:
          return SocialWebDayAndHourWidgetSchema
        case WidgetType.AUDIENCE_DAY_AND_HOUR:
          return AudienceDayAndHourWidgetSchema
        case WidgetType.SOCIAL_WEB_BRAND_PASSION_INDEX:
          return SocialWebBrandPassionIndexWidgetSchema
        case WidgetType.RICH_TEXT:
          return RichTextWidgetSchema
        case WidgetType.SOCIAL_WEB_POPULAR_LINKS:
          return SocialWebPopularLinksWidgetSchema
        case WidgetType.AUDIENCE_POPULAR_LINKS:
          return AudiencePopularLinksWidgetSchema
        case WidgetType.SOCIAL_WEB_CHANNELS:
          return SocialWebChannelsWidgetSchema
        case WidgetType.SOCIAL_WEB_TAGS:
          return SocialWebTagsWidgetSchema
        case WidgetType.HTML:
          return HTMLWidgetSchema
        case WidgetType.SOCIAL_WEB_LANGUAGE:
          return SocialWebLanguageWidgetSchema
        case WidgetType.AUDIENCE_LANGUAGE:
          return AudienceLanguageWidgetSchema
        case WidgetType.AUDIENCE_USAGE_AND_ATTITUDE:
          return AudienceUsageAndAttitudeWidgetSchema
        default:
          console.warn(
            `Unsupported widget type ${widget.type} in schema transformation for saved report`
          )
          return WidgetSchema // equivalent of no schema validation
      }
    })
  )
  /* END */
})
export default DashboardSchema
export type Dashboard = InferType<typeof DashboardSchema>
// RESERVE BELOW
